import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaSearch, FaCalendar, FaTag, FaStar, FaEye, FaShare, FaArrowRight } from 'react-icons/fa';
import { API_ENDPOINTS } from '../config/api';
import { getImageUrl, safeJsonParse } from '../utils/imageUtils';
import ImagePlaceholder from './ImagePlaceholder';
import Header from './Header';
import Footer from './Footer';

const WeeklyHighlightsList = () => {
  const navigate = useNavigate();
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    month: '',
    year: '',
    tags: '',
    featured: false
  });
  const [featuredHighlights, setFeaturedHighlights] = useState([]);

  useEffect(() => {
    fetchHighlights();
  }, []);

  const fetchHighlights = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.WEEKLY_HIGHLIGHTS_PUBLIC);
      const allHighlights = response.data;
      
      // Separate featured highlights
      const featured = allHighlights.filter(highlight => highlight.featured && highlight.status === 'published');
      const regular = allHighlights.filter(highlight => highlight.status === 'published');
      
      setFeaturedHighlights(featured);
      setHighlights(regular);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch weekly highlights:', err);
      setError(err.response?.data?.error || 'Failed to fetch weekly highlights');
      setLoading(false);
    }
  };

  const filteredHighlights = highlights.filter(highlight => {
    const matchesSearch = highlight.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         highlight.subtitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         highlight.coveragePeriod?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesMonth = !filters.month || new Date(highlight.coverage_period_start).getMonth() === parseInt(filters.month);
    const matchesYear = !filters.year || new Date(highlight.coverage_period_start).getFullYear() === parseInt(filters.year);
    const matchesTags = !filters.tags || highlight.tags?.some(tag => tag.toLowerCase().includes(filters.tags.toLowerCase()));
    
    return matchesSearch && matchesMonth && matchesYear && matchesTags;
  });

  const getMonthOptions = () => {
    const months = [
      { value: '', label: 'All Months' },
      { value: '0', label: 'January' },
      { value: '1', label: 'February' },
      { value: '2', label: 'March' },
      { value: '3', label: 'April' },
      { value: '4', label: 'May' },
      { value: '5', label: 'June' },
      { value: '6', label: 'July' },
      { value: '7', label: 'August' },
      { value: '8', label: 'September' },
      { value: '9', label: 'October' },
      { value: '10', label: 'November' },
      { value: '11', label: 'December' }
    ];
    return months;
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [{ value: '', label: 'All Years' }];
    
    for (let year = currentYear; year >= currentYear - 5; year--) {
      years.push({ value: year.toString(), label: year.toString() });
    }
    
    return years;
  };

  const formatCoveragePeriod = (startDate, endDate) => {
    if (!startDate || !endDate) return 'Coverage Period Not Set';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'Coverage Period Not Set';
    
    const options = { month: 'short', day: 'numeric' };
    return `Week of ${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  };

  const handleShare = async (highlight) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: highlight.title,
          text: highlight.subtitle || highlight.metaDescription,
          url: window.location.origin + `/weekly-highlights/${highlight.custom_url || highlight.id}`
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
              const url = window.location.origin + `/weekly-highlights/${highlight.custom_url || highlight.id}`;
      navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading weekly highlights...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">❌ Error Loading Weekly Highlights</h4>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      background: 'linear-gradient(to right, #91eae4, #86a8e7, #7f7fd5)',
      minHeight: '100vh',
      fontFamily: '"Quicksand", sans-serif'
    }}>
      {/* Header Navigation */}
      <Header />
      
      {/* Header Section */}
      <div className="container mb-5" style={{ paddingTop: '2rem' }}>
        <div className="row align-items-center">
          <div className="col-12 text-center">
            <h1 className="display-6 fw-bold text-white mb-2">Weekly Highlights</h1>
            <p className="text-white-50 mb-0" style={{ fontSize: '1.1rem', maxWidth: '600px', margin: '0 auto' }}>
              Stay updated with the latest insights, highlights, and developments from our community
            </p>
          </div>
        </div>
      </div>

      {/* Featured Highlights Row */}
      {featuredHighlights.length > 0 && (
        <div className="container mb-5">
          <div className="row">
            <div className="col-12">
              <h3 className="text-white mb-4 text-center">
                <FaStar style={{ color: '#ffd700', marginRight: '0.5rem' }} />
                Featured Highlights
              </h3>
            </div>
          </div>
          <div className="row g-4">
            {featuredHighlights.slice(0, 3).map((highlight) => (
              <div key={highlight.id} className="col-lg-4 col-md-6">
                <div className="card h-100 shadow-lg" style={{
                  border: 'none',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.1)';
                }}
                onClick={() => navigate(`/weekly-highlights/${highlight.custom_url || highlight.id}`)}>
                                     {/* Featured Badge */}
                   <div style={{
                     position: 'absolute',
                     top: '1rem',
                     left: '1rem',
                     zIndex: 2,
                     background: 'linear-gradient(135deg, #ffd700, #ffed4e)',
                     color: '#1f2937',
                     padding: '0.5rem 1rem',
                     borderRadius: '20px',
                     fontSize: '0.75rem',
                     fontWeight: '700',
                     boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)'
                   }}>
                     <FaStar style={{ marginRight: '0.25rem' }} />
                     FEATURED
                   </div>
                   
                                       {/* Cover Image */}
                    <div style={{ position: 'relative', height: '280px', overflow: 'hidden' }}>
                      {highlight.cover_image ? (
                        <img
                          src={getImageUrl(highlight.cover_image)}
                          alt={highlight.title}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block'
                          }}
                        />
                      ) : (
                        <ImagePlaceholder 
                          width="100%" 
                          height="100%" 
                          text="📰 Weekly Bulletin"
                        />
                      )}
                    </div>
                   
                                       {/* Card Body */}
                    <div style={{ 
                      padding: '1.5rem', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '1.5rem',
                      flex: 1 
                    }}>
                      <div>
                        <div className="d-flex flex-wrap gap-2">
                          <span style={{
                            background: 'linear-gradient(135deg, #7f7fd5, #86a8e7)',
                            color: 'white',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600'
                          }}>
                            {formatCoveragePeriod(highlight.coverage_period_start, highlight.coverage_period_end)}
                          </span>
                          {highlight.weekly_edition && (
                            <span style={{
                              background: 'linear-gradient(135deg, #10b981, #059669)',
                              color: 'white',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}>
                              Edition {highlight.weekly_edition}
                            </span>
                          )}
                          {highlight.coverage_month && (
                            <span style={{
                              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                              color: 'white',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: '600'
                            }}>
                              {highlight.coverage_month}
                            </span>
                          )}
                        </div>
                      </div>
                     
                     <h4 style={{ 
                       fontSize: '1.5rem', 
                       textTransform: 'capitalize',
                       fontWeight: '600',
                       color: '#333',
                       margin: 0,
                       lineHeight: '1.3'
                     }}>
                       {highlight.title}
                     </h4>
                    
                                         {highlight.subtitle && (
                       <p style={{
                         color: '#666',
                         fontSize: '0.9rem',
                         lineHeight: '1.6',
                         margin: 0,
                         flex: 1
                       }}>
                         {highlight.subtitle}
                       </p>
                     )}
                     
                     <div style={{ 
                       display: 'flex', 
                       justifyContent: 'space-between', 
                       alignItems: 'center',
                       marginTop: 'auto'
                     }}>
                       <button
                         style={{
                           background: 'none',
                           border: 'none',
                           color: '#7f7fd5',
                           fontSize: '0.875rem',
                           fontWeight: '600',
                           cursor: 'pointer',
                           display: 'flex',
                           alignItems: 'center',
                           gap: '0.5rem',
                           padding: 0
                         }}
                         onClick={(e) => {
                           e.stopPropagation();
                           navigate(`/weekly-highlights/${highlight.custom_url || highlight.id}`);
                         }}
                       >
                         Read Full Bulletin <FaArrowRight style={{ fontSize: '0.75rem' }} />
                       </button>
                       
                       <button
                         style={{
                           background: 'none',
                           border: 'none',
                           color: '#666',
                           cursor: 'pointer',
                           padding: '0.5rem',
                           borderRadius: '50%',
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center'
                         }}
                         onClick={(e) => {
                           e.stopPropagation();
                           handleShare(highlight);
                         }}
                         title="Share"
                       >
                         <FaShare style={{ fontSize: '0.75rem' }} />
                       </button>
                     </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="container mb-5">
        <div className="card" style={{ borderRadius: '16px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <div className="input-group">
                  <span className="input-group-text" style={{ background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                    <FaSearch />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search highlights..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ border: '1px solid #dee2e6' }}
                  />
                </div>
              </div>
              <div className="col-md-2">
                <select
                  className="form-select"
                  value={filters.month}
                  onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value }))}
                  style={{ border: '1px solid #dee2e6' }}
                >
                  {getMonthOptions().map(month => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <select
                  className="form-select"
                  value={filters.year}
                  onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
                  style={{ border: '1px solid #dee2e6' }}
                >
                  {getYearOptions().map(year => (
                    <option key={year.value} value={year.value}>{year.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Filter by tags..."
                  value={filters.tags}
                  onChange={(e) => setFilters(prev => ({ ...prev, tags: e.target.value }))}
                  style={{ border: '1px solid #dee2e6' }}
                />
              </div>
              <div className="col-md-2">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="featuredFilter"
                    checked={filters.featured}
                    onChange={(e) => setFilters(prev => ({ ...prev, featured: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="featuredFilter">
                    Featured Only
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* All Highlights Grid */}
      <div className="container mb-5">
        <div className="row">
          <div className="col-12">
            <h3 className="text-white mb-4 text-center">All Highlights</h3>
          </div>
        </div>
        
        {filteredHighlights.length === 0 ? (
          <div className="text-center py-5">
            <div style={{ fontSize: '4rem', color: 'rgba(255,255,255,0.5)', marginBottom: '1rem' }}>📭</div>
            <h3 className="text-white mb-2">No Highlights Found</h3>
            <p className="text-white-50">
              {searchTerm || Object.values(filters).some(f => f !== '' && f !== false)
                ? 'Try adjusting your filters or search terms.'
                : 'Check back later for new weekly highlights.'
              }
            </p>
          </div>
        ) : (
          <div className="row g-4">
            {filteredHighlights.map((highlight) => (
              <div key={highlight.id} className="col-lg-4 col-md-6">
                <div className="card h-100 shadow" style={{
                  border: 'none',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 15px 35px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 5px 15px rgba(0, 0, 0, 0.08)';
                }}
                onClick={() => navigate(`/weekly-highlights/${highlight.custom_url || highlight.id}`)}>
                  
                                     {/* Cover Image */}
                   <div style={{ position: 'relative', height: '280px', overflow: 'hidden' }}>
                     {highlight.cover_image ? (
                       <img
                         src={getImageUrl(highlight.cover_image)}
                         alt={highlight.title}
                         style={{
                           width: '100%',
                           height: '100%',
                           objectFit: 'cover',
                           display: 'block'
                         }}
                       />
                     ) : (
                       <ImagePlaceholder 
                         width="100%" 
                         height="100%" 
                         text="📰 Weekly Bulletin"
                       />
                     )}
                   </div>
                   
                   {/* Card Body */}
                   <div style={{ 
                     padding: '1.5rem', 
                     display: 'flex', 
                     flexDirection: 'column', 
                     gap: '1.5rem',
                     flex: 1 
                   }}>
                     <div>
                       <div className="d-flex flex-wrap gap-2">
                         <span style={{
                           background: 'linear-gradient(135deg, #7f7fd5, #86a8e7)',
                           color: 'white',
                           padding: '0.25rem 0.75rem',
                           borderRadius: '12px',
                           fontSize: '0.75rem',
                           fontWeight: '600'
                         }}>
                           {formatCoveragePeriod(highlight.coverage_period_start, highlight.coverage_period_end)}
                         </span>
                         {highlight.weekly_edition && (
                           <span style={{
                             background: 'linear-gradient(135deg, #10b981, #059669)',
                             color: 'white',
                             padding: '0.25rem 0.75rem',
                             borderRadius: '12px',
                             fontSize: '0.75rem',
                             fontWeight: '600'
                           }}>
                             Edition {highlight.weekly_edition}
                           </span>
                         )}
                         {highlight.coverage_month && (
                           <span style={{
                             background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                             color: 'white',
                             padding: '0.25rem 0.75rem',
                             borderRadius: '12px',
                             fontSize: '0.75rem',
                             fontWeight: '600'
                           }}>
                             {highlight.coverage_month}
                           </span>
                         )}
                       </div>
                     </div>
                    
                    <h4 style={{ 
                      fontSize: '1.5rem', 
                      textTransform: 'capitalize',
                      fontWeight: '600',
                      color: '#333',
                      margin: 0,
                      lineHeight: '1.3'
                    }}>
                      {highlight.title}
                    </h4>
                    
                    {highlight.subtitle && (
                      <p style={{
                        color: '#666',
                        fontSize: '0.9rem',
                        lineHeight: '1.6',
                        margin: 0,
                        flex: 1
                      }}>
                        {highlight.subtitle}
                      </p>
                    )}
                    
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginTop: 'auto'
                    }}>
                      <button
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#7f7fd5',
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: 0
                        }}
                                                 onClick={(e) => {
                           e.stopPropagation();
                           navigate(`/weekly-highlights/${highlight.custom_url || highlight.id}`);
                         }}
                      >
                        Read Full Highlight <FaArrowRight style={{ fontSize: '0.75rem' }} />
                      </button>
                      
                      <button
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#666',
                          cursor: 'pointer',
                          padding: '0.5rem',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShare(highlight);
                        }}
                        title="Share"
                      >
                        <FaShare style={{ fontSize: '0.75rem' }} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Spacing */}
      <div style={{ paddingBottom: '3rem' }}></div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default WeeklyHighlightsList;

